import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { Mandant } from '../../../../projects/kiene-core/src/lib/components/mandanten/classes/mandant';

@Component({
    selector: 'kiene-save-for-all-dialog',
    templateUrl: './save-for-all-dialog.component.html',
    styleUrls: ['./save-for-all-dialog.component.css'],
    host: { '(window:keyup)': 'keyListener($event)' },
})
export class SaveForAllDialogComponent implements OnInit {
    mandanten: Mandant[] = [];
    selectedMandantenIds: number[] = [];

    mandantCtrl: UntypedFormControl[] = [];
    bezeichnungCtrl: { [key: number]: UntypedFormControl } = {};
    einzelpreisCtrl: { [key: number]: UntypedFormControl } = {};
    verkaufspreisCtrl: { [key: number]: UntypedFormControl } = {};
    margeCtrl: { [key: number]: UntypedFormControl } = {};

    buttonText = 'Speichern';
    showPrices = false;
    showBezeichnung = false; // ✅ NEU: Standardmäßig Bezeichnung ausblenden
    usePricesFormat = false; // Wenn true, wird immer das Objekt mit mandanten_ids, preise, bezeichnungen zurückgegeben
    mandantPreise: Map<number, { einzelpreis?: number; verkaufspreis?: number }> = new Map();
    mandantBezeichnungen: Map<number, string> = new Map();

    // Optional: Felder mit den aktuellen Änderungen vorbefüllen (statt mit bestehenden Werten je Mandant)
    private prefillWithDefaults = false;
    private prefillBezeichnung: string | null = null;
    private prefillEinzelpreis: number | null = null;
    private prefillVerkaufspreis: number | null = null;
    private prefillMarge: number | null = null;
    
    // Flags um zu verhindern, dass Berechnungen sich gegenseitig auslösen
    isCalculatingVerkaufspreis: { [key: number]: boolean } = {};
    isCalculatingMarge: { [key: number]: boolean } = {};
    private currentMandantId: number | null = null;

    constructor(
        public dialogRef: MatDialogRef<SaveForAllDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data,
        private localStorageService: LocalStorageService,
    ) {
        this.mandanten = this.data.availableMandanten;
        if (this.data?.buttonText) {
            this.buttonText = this.data.buttonText;
        }
        if (this.data?.showPrices) {
            this.showPrices = this.data.showPrices;
        }
        // ✅ NEU: showBezeichnung aus data übernehmen (standardmäßig false)
        if (this.data?.showBezeichnung !== undefined) {
            this.showBezeichnung = this.data.showBezeichnung;
        }
        if (this.data?.usePricesFormat !== undefined) {
            this.usePricesFormat = this.data.usePricesFormat;
        }

        if (this.data?.prefillWithDefaults) {
            this.prefillWithDefaults = true;
            this.prefillBezeichnung = this.data.prefillBezeichnung ?? null;
            this.prefillEinzelpreis = this.data.prefillEinzelpreis ?? null;
            this.prefillVerkaufspreis = this.data.prefillVerkaufspreis ?? null;
            this.prefillMarge = this.data.prefillMarge ?? null;
        }
        
        // Speichere Preise und Bezeichnungen in Maps für einfachen Zugriff
        if (this.mandanten) {
            for (const m of this.mandanten) {
                const mandantAny = m as any;
                if (mandantAny.einzelpreis !== undefined || mandantAny.verkaufspreis !== undefined) {
                    this.mandantPreise.set(m.mandant_id, {
                        einzelpreis: mandantAny.einzelpreis,
                        verkaufspreis: mandantAny.verkaufspreis
                    });
                }
                if (mandantAny.artikel_bezeichnung !== undefined && mandantAny.artikel_bezeichnung !== null) {
                    this.mandantBezeichnungen.set(m.mandant_id, mandantAny.artikel_bezeichnung);
                }
            }
        }
    }
    
    getMandantPreis(mandantId: number): { einzelpreis?: number; verkaufspreis?: number } | undefined {
        return this.mandantPreise.get(mandantId);
    }

    ngOnInit(): void {
        const currentMandant = this.localStorageService.getCurrentMandant();
        this.currentMandantId = currentMandant?.mandant_id ?? null;

        for (const m of this.mandanten) {
            this.mandantCtrl[m.mandant_id] = new UntypedFormControl(true);
            if (m.mandant_id === currentMandant.mandant_id) {
                this.mandantCtrl[m.mandant_id].disable();
            }
            
            // Erstelle FormControl für Bezeichnung
            // Wenn prefillWithDefaults aktiv ist, verwende die NEUE Bezeichnung (aus dem Formular)
            // Sonst verwende die existierende Bezeichnung des Mandanten
            const existingBezeichnung = this.mandantBezeichnungen.get(m.mandant_id) ?? '';
            const bezeichnung =
                (this.prefillWithDefaults && this.prefillBezeichnung !== null && this.prefillBezeichnung !== undefined && this.prefillBezeichnung.trim() !== '')
                    ? this.prefillBezeichnung  // NEU: Verwende neue Bezeichnung
                    : existingBezeichnung;     // Fallback: alte Bezeichnung
            this.bezeichnungCtrl[m.mandant_id] = new UntypedFormControl(bezeichnung);
            
            // Erstelle FormControls für Preise, wenn showPrices aktiv ist
            if (this.showPrices) {
                const preise = this.getMandantPreis(m.mandant_id);
                
                let einzelpreisInit: number | null = null;
                let verkaufspreisInit: number | null = null;
                let initialMarge: number | null = null;
                
                if (this.prefillWithDefaults && this.prefillEinzelpreis !== null && this.prefillEinzelpreis !== undefined) {
                    // Einkaufspreis wurde geändert: Verwende neuen Einkaufspreis
                    einzelpreisInit = this.prefillEinzelpreis;
                    
                    // WICHTIG: Verwende ursprüngliche Marge des jeweiligen Mandanten (aus dessen vorhandenen Preisen)
                    // nicht die Marge aus dem aktuellen Formular
                    if (preise?.einzelpreis && preise.einzelpreis > 0 && preise?.verkaufspreis && preise.verkaufspreis > 0) {
                        // Berechne ursprüngliche Marge aus den vorhandenen Preisen des Mandanten
                        initialMarge = ((preise.verkaufspreis - preise.einzelpreis) / preise.verkaufspreis) * 100;
                        initialMarge = Math.round(initialMarge * 100) / 100;
                        
                        // Berechne neuen Verkaufspreis basierend auf neuem Einkaufspreis und ursprünglicher Marge des Mandanten
                        if (initialMarge !== 100) {
                            verkaufspreisInit = einzelpreisInit / (1 - initialMarge / 100);
                            verkaufspreisInit = Math.round(verkaufspreisInit * 100) / 100;
                        } else {
                            verkaufspreisInit = null;
                        }
                    } else if (this.prefillMarge !== null && this.prefillMarge !== undefined) {
                        // Fallback: Wenn keine vorhandenen Preise, verwende Marge vom Formular
                        initialMarge = this.prefillMarge;
                        if (initialMarge !== 100) {
                            verkaufspreisInit = einzelpreisInit / (1 - initialMarge / 100);
                            verkaufspreisInit = Math.round(verkaufspreisInit * 100) / 100;
                        } else {
                            verkaufspreisInit = null;
                        }
                    } else {
                        // Fallback: Standard-Marge verwenden
                        initialMarge = 20;
                        verkaufspreisInit = einzelpreisInit / (1 - initialMarge / 100);
                        verkaufspreisInit = Math.round(verkaufspreisInit * 100) / 100;
                    }
                } else {
                    // Keine Änderung: Verwende vorhandene Preise
                    einzelpreisInit = (preise?.einzelpreis !== null && preise?.einzelpreis !== undefined)
                        ? preise.einzelpreis
                        : null;
                    verkaufspreisInit = (preise?.verkaufspreis !== null && preise?.verkaufspreis !== undefined)
                        ? preise.verkaufspreis
                        : null;
                    
                    // Berechne Marge aus vorhandenen Preisen des Mandanten
                    if (einzelpreisInit && einzelpreisInit > 0 && verkaufspreisInit && verkaufspreisInit > 0) {
                        initialMarge = ((verkaufspreisInit - einzelpreisInit) / verkaufspreisInit) * 100;
                        initialMarge = Math.round(initialMarge * 100) / 100;
                    } else if (this.prefillMarge !== null && this.prefillMarge !== undefined) {
                        // Fallback: Wenn keine vorhandenen Preise, verwende Marge vom Formular
                        initialMarge = this.prefillMarge;
                    } else {
                        initialMarge = 20; // Standard-Marge
                    }
                }

                this.einzelpreisCtrl[m.mandant_id] = new UntypedFormControl(einzelpreisInit);
                this.verkaufspreisCtrl[m.mandant_id] = new UntypedFormControl(verkaufspreisInit);
                this.margeCtrl[m.mandant_id] = new UntypedFormControl(initialMarge);
                
                // Initialisiere Flags
                this.isCalculatingVerkaufspreis[m.mandant_id] = false;
                this.isCalculatingMarge[m.mandant_id] = false;
                
                // Event Listener für Berechnungen
                this.setupPriceCalculations(m.mandant_id);
            }
        }
        this.updateSelectedMandanten();
    }

    private isMandantSelected(mandantId: number): boolean {
        const ctrl = this.mandantCtrl[mandantId];
        if (!ctrl) {
            return false;
        }
        if (mandantId === this.currentMandantId && ctrl.disabled) {
            return true;
        }
        return !!ctrl.value;
    }

    private getSelectedMandantenIds(): number[] {
        const ids: number[] = [];
        for (const m of this.mandanten) {
            const mandantId = Number(m.mandant_id);
            if (this.isMandantSelected(mandantId)) {
                ids.push(mandantId);
            }
        }
        if (this.currentMandantId != null) {
            const currentId = Number(this.currentMandantId);
            if (!ids.includes(currentId)) {
                ids.push(currentId);
            }
        }
        return ids;
    }

    updateSelectedMandanten() {
        this.selectedMandantenIds = this.getSelectedMandantenIds();
    }

    cancel() {
        this.dialogRef.close();
    }

    keyListener(event) {
        if (event.code === 'Enter') {
            this.saveForSelected();
        }
    }

    setupPriceCalculations(mandantId: number) {
        // Wenn Einkaufspreis oder Marge geändert wird → Verkaufspreis berechnen
        this.einzelpreisCtrl[mandantId].valueChanges.subscribe(() => {
            if (!this.isCalculatingVerkaufspreis[mandantId] && !this.isCalculatingMarge[mandantId]) {
                this.calculateVerkaufspreis(mandantId);
            }
        });
        
        this.margeCtrl[mandantId].valueChanges.subscribe(() => {
            if (!this.isCalculatingVerkaufspreis[mandantId] && !this.isCalculatingMarge[mandantId]) {
                this.calculateVerkaufspreis(mandantId);
            }
        });
        
        // Wenn Verkaufspreis geändert wird → Marge berechnen
        this.verkaufspreisCtrl[mandantId].valueChanges.subscribe(() => {
            if (!this.isCalculatingVerkaufspreis[mandantId] && !this.isCalculatingMarge[mandantId]) {
                this.calculateMarge(mandantId);
            }
        });
    }
    
    calculateVerkaufspreis(mandantId: number) {
        const einzelpreis = this.einzelpreisCtrl[mandantId]?.value;
        const marge = this.margeCtrl[mandantId]?.value;
        
        if (einzelpreis && einzelpreis > 0 && marge !== null && marge !== undefined) {
            this.isCalculatingVerkaufspreis[mandantId] = true;
            const verkaufspreis = einzelpreis / (1 - marge / 100);
            const gerundet = Math.round(verkaufspreis * 100) / 100;
            this.verkaufspreisCtrl[mandantId].setValue(gerundet, { emitEvent: false });
            this.isCalculatingVerkaufspreis[mandantId] = false;
        }
    }
    
    calculateMarge(mandantId: number) {
        const einzelpreis = this.einzelpreisCtrl[mandantId]?.value;
        const verkaufspreis = this.verkaufspreisCtrl[mandantId]?.value;
        
        if (einzelpreis && einzelpreis > 0 && verkaufspreis && verkaufspreis > 0) {
            this.isCalculatingMarge[mandantId] = true;
            const marge = ((verkaufspreis - einzelpreis) / verkaufspreis) * 100;
            const gerundet = Math.round(marge * 100) / 100;
            this.margeCtrl[mandantId].setValue(gerundet, { emitEvent: false });
            this.isCalculatingMarge[mandantId] = false;
        }
    }

    saveForSelected() {
        this.updateSelectedMandanten();
        // Wenn usePricesFormat aktiviert ist, verwende die spezielle Methode für Behandlungsartikel
        if (this.usePricesFormat) {
            this.saveForSelectedWithPrices();
            return;
        }
        
        // Sammle Bezeichnungen
        const bezeichnungen: { [key: number]: string } = {};
        for (const mandantId of this.selectedMandantenIds) {
            const bezeichnung = this.bezeichnungCtrl[mandantId]?.value;
            if (bezeichnung && bezeichnung.trim() !== '') {
                bezeichnungen[mandantId] = bezeichnung.trim();
            }
        }
        
        // Sammle Preise (nur wenn showPrices aktiviert ist)
        const preise: { [key: number]: { einzelpreis?: number | null; verkaufspreis?: number | null; marge?: number | null } } = {};
        if (this.showPrices) {
            for (const mandantId of this.selectedMandantenIds) {
                preise[mandantId] = {
                    einzelpreis: this.einzelpreisCtrl[mandantId]?.value || null,
                    verkaufspreis: this.verkaufspreisCtrl[mandantId]?.value || null,
                    marge: this.margeCtrl[mandantId]?.value || null
                };
            }
        }
        
        // Wenn keine Preise und keine Bezeichnungen vorhanden sind, gib direkt das Array zurück
        const hasBezeichnungen = Object.keys(bezeichnungen).length > 0;
        const hasPreise = Object.keys(preise).length > 0;
        
        if (!hasBezeichnungen && !hasPreise) {
            // Gib direkt das Array zurück für einfache Fälle
            this.dialogRef.close(this.selectedMandantenIds);
        } else {
            // Gib Objekt zurück mit mandanten_ids, preise und bezeichnungen
            const result: any = {
                mandanten_ids: this.selectedMandantenIds,
                preise: preise,
                bezeichnungen: bezeichnungen
            };
            this.dialogRef.close(result);
        }
    }

    saveForSelectedWithPrices() {
        this.updateSelectedMandanten();
        const selectedIds = this.getSelectedMandantenIds();
        const result: any = {
            mandanten_ids: selectedIds,
            preise: {},
            bezeichnungen: {},
        };

        for (const mandantId of selectedIds) {
            const bezeichnung = this.bezeichnungCtrl[mandantId]?.value;
            if (bezeichnung && bezeichnung.trim() !== '') {
                result.bezeichnungen[mandantId] = bezeichnung.trim();
            }
        }
        
        if (this.showPrices) {
            for (const mandantId of selectedIds) {
                result.preise[mandantId] = {
                    einzelpreis: this.einzelpreisCtrl[mandantId]?.value || null,
                    verkaufspreis: this.verkaufspreisCtrl[mandantId]?.value || null,
                    marge: this.margeCtrl[mandantId]?.value || null,
                };
            }
        }

        this.dialogRef.close(result);
    }
}
